import { lazy, useEffect, useState } from 'react'
import { NVButton } from '@nv/basic-components'
import { useNVModal } from '@nv/components'
import { MinusCloseIcon } from '@nv/images/design/minus-close-icon'
import { PlusOpenIcon } from '@nv/images/design/plus-open-icon'
import WarningIcon from '@nv/images/design/warning-icon'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import styles from './PositionAccordion.module.scss'
const CandidateEndorsementsList = lazy(() => import('@nv/lazy-loadable/lib/modals/CandidateEndorsementsList'))

export const PositionAccordion = ({
  positionLabel,
  position,
  children,
  isOpenByDefault,
  eventId,
  applied,
  event,
  numEndorsements,
  candidacy,
}) => {
  const { t, i18n } = useTranslation()
  const currentLanguage = i18n.language
  const [isOpen, setIsOpen] = useState(isOpenByDefault)
  const [hasInteracted, setHasInteracted] = useState(false)
  const navigate = useNavigate()
  const { NVModal: CandidateEndorsementsListModal, openModal: openCandidateEndorsementsListModal } = useNVModal()

  const toggleDropdown = () => {
    setIsOpen((prev) => !prev)
    setHasInteracted(true)
  }

  useEffect(() => {
    if (hasInteracted && isOpen) document.getElementById(`section-${positionLabel}`)?.focus({ preventScroll: true })
  }, [isOpen, hasInteracted, positionLabel])

  return (
    <>
      <CandidateEndorsementsListModal>
        <CandidateEndorsementsList
          event={event}
          numEndorsements={numEndorsements}
          candidacy={candidacy}
          position={position.texts[currentLanguage]}
        />
      </CandidateEndorsementsListModal>

      <div className={styles['container']}>
        <h2 className={styles['header']}>
          <div
            role='button'
            tabIndex={0}
            onClick={toggleDropdown}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                toggleDropdown()
              }
            }}
            aria-expanded={isOpen}
            aria-controls={`section-${positionLabel}`}
            id={`dropdown-header-${positionLabel}`}
            className={styles['toggle']}
          >
            <div className={styles['toggle__content']}>
              <div className={styles['toggle__title']}>{positionLabel}</div>
              {isOpen ? <MinusCloseIcon className={styles['chevron-icon--sm']} /> : <PlusOpenIcon className={styles['chevron-icon--sm']} />}
            </div>
            <div className={styles['toggle__actions']}>
              {applied && event?.endorsementsRequired && candidacy?.nominationState !== 'admin-declined' ? (
                <div
                  className={styles['endorsements']}
                  role='button'
                  tabIndex={0}
                  onClick={(e) => {
                    e.stopPropagation()
                    openCandidateEndorsementsListModal()
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      e.stopPropagation()
                      openCandidateEndorsementsListModal()
                    }
                  }}
                  aria-label={t('events.settings.endorsements')}
                >
                  <span>{event?.endorsementsRequired ? t('events.settings.endorsements') : ''}</span>
                  <span className={styles['endorsements__count']}>
                    {numEndorsements} {event?.endorsementThresholdRequired ? ` / ${event.endorsementThreshold}` : ''}
                  </span>
                </div>
              ) : null}
              <div className={styles['actions__button-group']}>
                {applied && event?.nominationState === 'started' && candidacy?.nominationState !== 'admin-declined' ? (
                  <div
                    className={candidacy?.nominationState === 'review-required' ? styles['warning-tooltip'] : styles['candidacy-actions']}
                    data-tooltip={
                      candidacy?.nominationState === 'review-required' ? t('events.profile.applicationResubmitConfirm') : undefined
                    }
                  >
                    {candidacy?.nominationState === 'review-required' && <WarningIcon color='var(--error-color)' width={32} height={32} />}
                    <NVButton
                      variant='greenButton'
                      height={40}
                      text={t('buttons.manageMyCandidacy')}
                      onClick={(e) => {
                        e.stopPropagation()
                        navigate(`/voting/voter/candidate-profile/events/${eventId}/questions/${position.qid}`)
                      }}
                    />
                  </div>
                ) : !applied && event?.nominationState === 'started' ? (
                  <NVButton
                    variant='secondary'
                    height={40}
                    text={t('events.candidateEvents.applyForPosition')}
                    onClick={(e) => {
                      e.stopPropagation()
                      navigate(`/voting/voter/candidate-profile/events/${eventId}/questions/${position.qid}`)
                    }}
                  />
                ) : applied && candidacy?.nominationState !== 'admin-declined' ? (
                  <NVButton
                    variant='secondary'
                    height={40}
                    text={t('buttons.viewProfile')}
                    onClick={(e) => {
                      e.stopPropagation()
                      navigate(`/voting/voter/event-details/${eventId}/questions/${position.qid}/candidates/${candidacy?.id}`)
                    }}
                  />
                ) : null}
              </div>
              {isOpen ? <MinusCloseIcon className={styles['chevron-icon--lg']} /> : <PlusOpenIcon className={styles['chevron-icon--lg']} />}
            </div>
          </div>
        </h2>

        <div
          id={`section-${positionLabel}`}
          role='region'
          aria-labelledby={`dropdown-header-${positionLabel}`}
          tabIndex={-1}
          className={clsx(styles['content'], {
            [styles['content--open']]: isOpen,
          })}
        >
          <div className={styles['content__inner']}>{children}</div>
        </div>
      </div>
    </>
  )
}
